import { useId, useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  COMP_COPY,
  COMP_MAX,
  COMP_START,
  COMP_STEP,
  compFlags,
  compHitSplit,
  compMoney,
  compPct,
  moveComp,
} from '@/lib/audition/comp-range'
import type {
  FollowUpAnswers,
  RangeQuestion,
} from '@/lib/audition/priority-followups'
import { setFollowUpRange } from '@/lib/audition/priority-followups'
import { cn } from '@/lib/utils'

/**
 * The one follow-up that is a pair of numbers: the floor they would seriously
 * consider and the realistic top they are hopeful for.
 *
 * Every rule and every sentence is in `lib/audition/comp-range.ts`. This file
 * draws what that module says a range is and reports drags back.
 *
 * Two native range inputs stacked over one drawn track, the technique the
 * proficiency slider in `components/ui` already uses here: the real inputs
 * are transparent and each owns the half of the track nearer its own thumb,
 * so a pointer always grabs the thumb it is closest to while the keyboard
 * still reaches both. Rolling our own drag handling instead would mean
 * re-implementing arrow keys, Home/End and the step grid that the native
 * control gives away.
 */
export function CompRangeField({
  question,
  answers,
  onChange,
  disabled,
}: {
  question: RangeQuestion
  answers: FollowUpAnswers
  onChange: (answers: FollowUpAnswers) => void
  disabled?: boolean
}) {
  const uid = useId()
  // The two explanations are long, and both are true whatever the thumbs are
  // on. Behind one control so the card stays readable — unlike the two
  // warnings below, which are about THIS answer and are never hidden.
  const [explaining, setExplaining] = useState(false)
  // What is DRAWN when nothing is stored. The starting position is ours, not
  // theirs, so it lives here and never in the answers bag: `setFollowUpRange`
  // is what turns it into an answer, and it only runs when a thumb moves.
  const range = answers.ranges?.[question.id] ?? COMP_START
  const flags = compFlags(range)
  const labelId = `${uid}-label`
  const tipsId = `${uid}-tips`
  const move = (which: 'min' | 'max', raw: string) =>
    onChange(
      setFollowUpRange(answers, question, moveComp(range, which, Number(raw))),
    )
  // Each input owns the part of the track nearer its own thumb. Stacked
  // without this the top one swallows every pointer press and the bottom
  // thumb can only be moved with a keyboard.
  const split = compHitSplit(range)
  // Which thumb has the keyboard. The real inputs are transparent and the
  // drawn thumbs are decoration, so without this the one control on this card
  // that a keyboard can reach is the one that never shows it has been reached
  // — and with two sliders stacked, no way to tell which the arrow keys move.
  const [focused, setFocused] = useState<'min' | 'max' | null>(null)

  const thumb = (value: number, label: string, which: 'min' | 'max') => (
    <input
      type="range"
      min={0}
      max={COMP_MAX}
      step={COMP_STEP}
      value={value}
      disabled={disabled}
      aria-label={label}
      // Without this a screen reader reads "300000", which is the number we
      // store rather than the number on screen.
      aria-valuetext={compMoney(value)}
      onChange={(e) => move(which, e.target.value)}
      onFocus={() => setFocused(which)}
      onBlur={() => setFocused(null)}
      className="absolute inset-0 size-full cursor-pointer opacity-0 disabled:cursor-not-allowed"
    />
  )

  return (
    <div className="space-y-2">
      <p id={labelId} className="text-sm text-muted-foreground text-pretty">
        {question.label}
      </p>

      {/* 44px tall, not the 8px of the drawn rail. Every other control in
          this widget holds a 44px tap target on purpose — the ladder rejected
          drag gestures over exactly this — and a drag control at 20px would
          be the one place the phone collects less. The rail and the thumbs
          are centred inside it; the whole box is what the finger gets. */}
      <div
        role="group"
        aria-labelledby={labelId}
        className="relative flex h-11 w-full items-center"
      >
        {/* Every drawn piece below mirrors the sibling slider's disabled
            tokens. The real inputs are transparent, so with these left at
            their live colours a submitting step shows a primary-coloured
            control that looks draggable and ignores the finger. */}
        <div
          className={cn(
            'h-2 w-full rounded-full bg-muted',
            disabled && 'bg-muted/60',
          )}
        />
        <div
          className={cn(
            'absolute h-2 rounded-full bg-primary/60',
            disabled && 'bg-muted-foreground/30',
          )}
          style={{
            left: `${compPct(range.min)}%`,
            width: `${compPct(range.max) - compPct(range.min)}%`,
          }}
        />
        <div
          className="absolute inset-0 z-20"
          style={{ clipPath: `inset(0 ${100 - split}% 0 0)` }}
        >
          {thumb(range.min, COMP_COPY.minLabel, 'min')}
        </div>
        <div
          className="absolute inset-0 z-30"
          style={{ clipPath: `inset(0 0 0 ${split}%)` }}
        >
          {thumb(range.max, COMP_COPY.maxLabel, 'max')}
        </div>
        {(['min', 'max'] as const).map((which) => (
          <div
            key={which}
            aria-hidden="true"
            // Named in the DOM the way the sibling slider names its fill: the
            // drawn thumb carries no role of its own, so this is the only
            // handle a test has on "the thing the applicant is looking at".
            data-thumb={which}
            data-focused={focused === which || undefined}
            className={cn(
              'pointer-events-none absolute z-40 size-4 rounded-full border-2 border-background bg-primary shadow',
              focused === which && 'ring-[3px] ring-ring/50',
              disabled && 'bg-muted-foreground/50',
            )}
            style={{ left: `calc(${compPct(range[which])}% - 0.5rem)` }}
          />
        ))}
      </div>

      <p
        aria-hidden="true"
        className="flex justify-between text-xs text-muted-foreground"
      >
        <span>{compMoney(0)}</span>
        <span>{compMoney(COMP_MAX)}</span>
      </p>

      {/* What the thumbs currently say, in words. The inputs carry the same
          two numbers as `aria-valuetext`, so this is for the eye only and
          would otherwise be read out twice. */}
      <p
        aria-hidden="true"
        className={cn(
          'flex justify-between gap-2 text-sm text-foreground',
          disabled && 'text-muted-foreground',
        )}
      >
        <span>
          <span
            className={cn(
              'font-semibold',
              flags.low && !disabled && 'text-destructive',
            )}
          >
            {compMoney(range.min)}
          </span>{' '}
          floor
        </span>
        <span>
          hopeful for{' '}
          <span
            className={cn(
              'font-semibold',
              flags.high && !disabled && 'text-amber-600 dark:text-amber-400',
            )}
          >
            {compMoney(range.max)}
          </span>
        </span>
      </p>

      {/* Always mounted AND always rendered, so the region is in the
          accessibility tree before it has anything to announce. `empty:hidden`
          sat here and defeated the point: `display: none` takes a node out of
          that tree, so the flag arriving looked to a screen reader like a
          populated region appearing rather than a change inside one, and
          nothing was read out. `empty:m-0` collapses the gap instead. */}
      <div aria-live="polite" className="space-y-1 empty:m-0">
        {flags.low && (
          <p
            className={cn(
              'text-xs text-destructive text-pretty',
              disabled && 'text-muted-foreground',
            )}
          >
            {COMP_COPY.lowFlag}
          </p>
        )}
        {flags.high && (
          <p
            className={cn(
              'text-xs text-pretty',
              // Gated rather than overridden: twMerge keeps variants apart,
              // so an unprefixed `text-muted-foreground` never displaces a
              // `dark:` colour and the warning stays amber in dark mode.
              !disabled && 'text-amber-600 dark:text-amber-400',
              disabled && 'text-muted-foreground',
            )}
          >
            {COMP_COPY.highFlag}
          </p>
        )}
      </div>

      <Button
        type="button"
        variant="ghost"
        size="sm"
        disabled={disabled}
        aria-expanded={explaining}
        aria-controls={tipsId}
        onClick={() => setExplaining((open) => !open)}
      >
        {COMP_COPY.explain}
      </Button>
      <div id={tipsId} hidden={!explaining} className="space-y-1">
        <p className="text-xs text-muted-foreground text-pretty">
          {COMP_COPY.minTip}
        </p>
        <p className="text-xs text-muted-foreground text-pretty">
          {COMP_COPY.maxTip}
        </p>
      </div>
    </div>
  )
}
